import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  useColorModeValue,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import { GreshamHeader } from "../components/GreshamHeader";
import { GreshamFooter } from "../components/GreshamFooter";

const Fund: React.FC = () => {
  const textColor = useColorModeValue("gray.800", "white");
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedInvestorType, setSelectedInvestorType] = useState<string>("");
  const [isAcknowledged, setIsAcknowledged] = useState<boolean>(false);
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.pageYOffset;
        const rate = scrolled * -0.5;
        parallaxRef.current.style.transform = `translateY(${rate}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: ""
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Allow only phone-like characters for the phone field
    if (name === "phone") {
      const cleaned = value.replace(/[^0-9+\s-]/g, "");
      setFormData(prev => ({
        ...prev,
        phone: cleaned,
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (PDF only)
      if (file.type !== 'application/pdf') {
        toast({
          title: "Invalid File Type",
          description: "Please upload a PDF file for the certificate.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        e.target.value = '';
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Certificate file must be less than 10MB.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        e.target.value = '';
        return;
      }
      setCertificateFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic client-side validation for investor acknowledgement
    if (!selectedInvestorType || !isAcknowledged) {
      toast({
        title: "Additional information required",
        description: "Please select your investor type and acknowledge the disclaimer.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // For sophisticated investors, certificate upload is mandatory
    if (selectedInvestorType === "sophisticated" && !certificateFile) {
      toast({
        title: "Certificate required",
        description: "Please upload your sophisticated investor confirmation letter / certificate.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const apiUrl = process.env.REACT_APP_FORM_API_URL;

    if (!apiUrl) {
      toast({
        title: "Form API not configured",
        description: "Please configure REACT_APP_FORM_API_URL.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const payload: Record<string, any> = {
        ...formData,
        selectedInvestorType,
        isAcknowledged,
      };

      // Include filename if a certificate is attached
      if (certificateFile) {
        payload.certificateFileName = certificateFile.name;
      }

      // Build multipart/form-data so the backend can receive the file
      const formDataToSend = new FormData();
      formDataToSend.append("formType", "fund");

      Object.entries(payload).forEach(([key, value]) => {
        formDataToSend.append(key, String(value));
      });

      if (certificateFile) {
        formDataToSend.append("certificate", certificateFile, certificateFile.name);
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Fund form submission error", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });

        let errorMessage = "Something went wrong while sending your enquiry. Please try again.";
        try {
          const parsed = JSON.parse(errorText);
          if (parsed && typeof parsed.message === "string") {
            errorMessage = parsed.message;
          }
        } catch {
          // ignore JSON parse errors
        }

        throw new Error(errorMessage);
      }

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
      });
      setSelectedInvestorType("");
      setIsAcknowledged(false);
      setCertificateFile(null);

      onOpen();
    } catch (error: any) {
      console.error("Fund form submission failed", error);
      toast({
        title: "Submission failed",
        description: error?.message || "Something went wrong while sending your enquiry. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      bg="white"
      css={{
        overscrollBehavior: "none",
        "-webkit-overflow-scrolling": "touch"
      }}
    >
      <GreshamHeader />

      {/* Hero Section with Background Image */}
      <Box
        as="section"
        w="full"
        position="relative"
        h="100vh"
        minH="600px"
        overflow="hidden"
        clipPath="inset(0)"
      >
        {/* Background Image Container with Parallax */}
        <Box
          ref={parallaxRef}
          position="fixed"
          top="0"
          left="0"
          right="0"
          height="120vh"
          backgroundImage="url('/background/cj-65kwzlfW6QI-unsplash.jpg')"
          backgroundSize="cover"
          backgroundPosition="center center"
          backgroundRepeat="no-repeat"
          filter="brightness(0.7)"
          willChange="transform"
        />

        {/* Text Container - Overlay on background for mobile, positioned for desktop */}
        <Box
          position={{ base: "absolute", lg: "absolute" }}
          top={{ base: "50%", lg: 0 }}
          left={{ base: 0, lg: 0 }}
          w={{ base: "full", lg: "70%" }}
          h={{ base: "auto", lg: "calc(120px + 300px)", xl: "calc(140px + 300px)", "2xl": "calc(160px + 300px)" }}
          display="flex"
          alignItems={{ base: "center", lg: "flex-end" }}
          justifyContent={{ base: "center", lg: "flex-start" }}
          zIndex={100}
          bg={{ base: "transparent", lg: "transparent" }}
          mb={{ base: 0, lg: 0 }}
          order={{ base: 1, lg: 0 }}
          pt={{ base: 0, md: 0, lg: "120px", xl: "140px", "2xl": "160px" }}
          pb={{ base: 0, md: 0, lg: 0 }}
          px={{ base: 6, sm: 8, md: 12, lg: 16 }}
          transform={{ base: "translateY(-50%)", lg: "none" }}
        >
          <VStack spacing={3} align="flex-start" maxW={{ base: "90%", sm: "80%", md: "700px", lg: "800px" }} ml={{ base: "auto", lg: "10%" }} mr="auto" textAlign="left" justify="center">
            {/* Space placeholder like homepage */}
            <Text
              fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
              color="white"
              fontFamily="'Carmen Sans Regular', sans-serif"
              fontWeight="300"
              letterSpacing="0.05em"
              className="slide-in-left"
            >
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </Text>

            {/* Main Heading */}
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
              fontWeight="bold"
              color="white"
              fontFamily="'Carmen Sans Bold', sans-serif"
              lineHeight="1.15"
              maxW={{ base: "100%", md: "900px", lg: "1000px" }}
              className="slide-in-left-delay-1"
            >
              Mercurian Equity Fund
            </Heading>
          </VStack>
        </Box>
      </Box>

      {/* About Section */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
        bg="white"
        mt="-80px"
        position="relative"
        zIndex={20}
        borderRadius="3xl 3xl 0 0"
        boxShadow="0 -20px 40px rgba(0,0,0,0.05)"
      >
        <Box
          px={{ base: 6, md: 12, lg: 16 }}
          w="full"
        >
          {/* Welcome Heading */}
          <Box mb={12}>
            <Box
              position="relative"
              display="inline-block"
              cursor="pointer"
              _hover={{
                "& .underline-animation": {
                  transform: "scaleX(1)"
                }
              }}
            >
              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color={textColor}
                fontFamily="'Carmen Sans Bold', sans-serif"
                letterSpacing="0.08em"
                textTransform="uppercase"
                textAlign="left"
              >
                OVERVIEW
              </Heading>
              <Box
                className="underline-animation"
                position="absolute"
                bottom="-8px"
                left={0}
                w="100%"
                h="1px"
                bg={textColor}
                transform="scaleX(0)"
                transformOrigin="left"
                transition="transform 0.3s ease"
              />
            </Box>
          </Box>

          {/* Two Column Content Layout */}
          <Box w="full">
            {/* Introduction Text */}
            <Text
              fontSize={{ base: "sm", md: "md" }}
              color={textColor}
              fontFamily="'Carmen Sans Light', sans-serif"
              lineHeight="1.6"
              fontWeight="300"
              opacity={0.8}
              mb={8}
              maxW="full"
            >
              A five-year closed ended fund, investing in unlisted Australian companies operating in the lower and middle market through a long term own and operate strategy. Exposure to this asset class can offer potential benefits to investors looking to diversify their portfolio as it could lead to potentially higher returns whilst mitigating risks over the long term.
            </Text>

          </Box>
        </Box>
      </Box>

      {/* Focus Section */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
        mb={{ base: 8, md: 12 }}
      >
        <Box
          bg="#f7f7f7"
          borderRadius="lg"
          py={{ base: 12, md: 16 }}
          px={{ base: 6, md: 12, lg: 16 }}
        >
          {/* Focus Heading */}
          <Box mb={12}>
            <Box
              position="relative"
              display="inline-block"
              cursor="pointer"
              _hover={{
                "& .underline-animation": {
                  transform: "scaleX(1)"
                }
              }}
            >
              <Heading
                as="h3"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color={textColor}
                fontFamily="'Carmen Sans Bold', sans-serif"
                letterSpacing="0.08em"
                textTransform="uppercase"
                textAlign="left"
              >
                FOCUS
              </Heading>
              <Box
                className="underline-animation"
                position="absolute"
                bottom="-8px"
                left={0}
                w="100%"
                h="1px"
                bg={textColor}
                transform="scaleX(0)"
                transformOrigin="left"
                transition="transform 0.3s ease"
              />
            </Box>
          </Box>

          {/* Fund Information Content */}
          <Box w="full">
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, lg: 12 }}>
              {/* Left Column */}
              <Box>
                <Box mb={6}>
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="500"
                    color={textColor}
                    mb={3}
                    fontFamily="'Carmen Sans Medium', sans-serif"
                  >
                    Fund Opportunity
                  </Text>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="300"
                    opacity={0.8}
                  >
                    Acquisition of units in the asset owning entity. Our investment strategy is to maximise operational efficiency and unlock value of underlying assets through our operating strategy and flexible capital structures, while providing adequate downside protection.
                  </Text>
                </Box>

                <Box mb={6}>
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="500"
                    color={textColor}
                    mb={3}
                    fontFamily="'Carmen Sans Medium', sans-serif"
                  >
                    Investment Term
                  </Text>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="300"
                    opacity={0.8}
                  >
                    The investment term is five (5) years. Subject to the Trustee's absolute discretion in consultation with the Portfolio Manager to accept reduced terms.
                  </Text>
                </Box>

                <Box>
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="500"
                    color={textColor}
                    mb={3}
                    fontFamily="'Carmen Sans Medium', sans-serif"
                  >
                    Benefits and Risks
                  </Text>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="300"
                    opacity={0.8}
                  >
                    Upside: Exposure to the Australian private equity markets, with potential for high upside. Portfolio diversification through investment strategies in differentiated market segments.
                  </Text>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="300"
                    opacity={0.8}
                    mt={2}
                  >
                    Downside: All investments carry risks and an investment in the Fund is not a bank deposit. Withdrawals cannot be made at call.
                  </Text>
                </Box>
              </Box>

              {/* Right Column */}
              <Box>
                <Box mb={6}>
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="500"
                    color={textColor}
                    mb={3}
                    fontFamily="'Carmen Sans Medium', sans-serif"
                  >
                    Liquidity & withdrawal
                  </Text>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="300"
                    opacity={0.8}
                  >
                    Access and liquidation of funds during the recommended investment term is at the discretion of the Fund Manager.
                  </Text>
                </Box>

                <Box>
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="500"
                    color={textColor}
                    mb={3}
                    fontFamily="'Carmen Sans Medium', sans-serif"
                  >
                    Disclaimer / investment eligibility and how to invest
                  </Text>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="300"
                    opacity={0.8}
                  >
                    Mercurian Equity investments are designed for sophisticated and institutional investors. This asset class will be ideal for investors such as Superannuation Funds, Self–Managed Super Funds (SMSF) and Private Equity Investors. Through exceptional standards of service and diligent investment management we aim to deliver outcomes that outperform the broader Australian market.
                  </Text>
                </Box>
              </Box>
            </SimpleGrid>
          </Box>
        </Box>
      </Box>

      {/* Contact Section */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
        bg="white"
      >
        <Box
          px={{ base: 6, md: 12, lg: 16 }}
          w="full"
        >
          {/* Open for subscription section */}
          <Box mb={12}>
            <Heading
              as="h3"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              color={textColor}
              fontFamily="'Carmen Sans Bold', sans-serif"
              letterSpacing="0.08em"
              textTransform="uppercase"
              textAlign="left"
              mb={6}
            >
              OPEN FOR SUBSCRIPTION
            </Heading>
            
            <Text
              fontSize={{ base: "sm", md: "md" }}
              color={textColor}
              fontFamily="'Carmen Sans Light', sans-serif"
              lineHeight="1.6"
              fontWeight="300"
              opacity={0.8}
              textAlign="left"
            >
              This investment product is available exclusively to institutional and wholesale investors
            </Text>
          </Box>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 12, lg: 20 }}>
          {/* Left Column - Contact Form */}
          <Box>
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
                {/* First Name */}
                <Box>
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    mb={2}
                    textTransform="uppercase"
                    letterSpacing="1px"
                    fontFamily="'Carmen Sans Light', sans-serif"
                  >
                    FIRST NAME
                  </Text>
                  <Box
                    borderBottom="1px solid"
                    borderColor="gray.300"
                    pb={2}
                  >
                    <input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        fontSize: '16px',
                        fontFamily: "'Carmen Sans Light', sans-serif"
                      }}
                    />
                  </Box>
                </Box>

              {/* Last Name */}
              <Box>
                <Text
                  fontSize="sm"
                  color="gray.500"
                  mb={2}
                  textTransform="uppercase"
                  letterSpacing="1px"
                  fontFamily="'Carmen Sans Light', sans-serif"
                >
                  LAST NAME
                </Text>
                <Box
                  borderBottom="1px solid"
                  borderColor="gray.300"
                  pb={2}
                >
                  <input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: '16px',
                      fontFamily: "'Carmen Sans Light', sans-serif"
                    }}
                  />
                </Box>
              </Box>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
              {/* Email */}
              <Box>
                <Text
                  fontSize="sm"
                  color="gray.500"
                  mb={2}
                  textTransform="uppercase"
                  letterSpacing="1px"
                  fontFamily="'Carmen Sans Light', sans-serif"
                >
                  YOUR EMAIL
                </Text>
                <Box
                  borderBottom="1px solid"
                  borderColor="gray.300"
                  pb={2}
                >
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: '16px',
                      fontFamily: "'Carmen Sans Light', sans-serif"
                    }}
                  />
                </Box>
              </Box>

              {/* Phone */}
              <Box>
                <Text
                  fontSize="sm"
                  color="gray.500"
                  mb={2}
                  textTransform="uppercase"
                  letterSpacing="1px"
                  fontFamily="'Carmen Sans Light', sans-serif"
                >
                  YOUR PHONE
                </Text>
                <Box
                  borderBottom="1px solid"
                  borderColor="gray.300"
                  pb={2}
                >
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    inputMode="tel"
                    pattern="[0-9+\s-]*"
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: '16px',
                      fontFamily: "'Carmen Sans Light', sans-serif"
                    }}
                  />
                </Box>
              </Box>
            </SimpleGrid>

            {/* Type of Investors */}
            <Box mb={8}>
              <Text
                fontSize="sm"
                color="gray.500"
                mb={2}
                textTransform="uppercase"
                letterSpacing="1px"
                fontFamily="'Carmen Sans Light', sans-serif"
              >
                TYPE OF INVESTORS
              </Text>
              <VStack spacing={3} align="flex-start">
                <Box display="flex" alignItems="center">
                  <Box
                    w="20px"
                    h="20px"
                    borderRadius="50%"
                    border="2px solid"
                    borderColor={selectedInvestorType === "sophisticated" ? "gray.500" : "gray.500"}
                    mr={3}
                    cursor="pointer"
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    _hover={{ borderColor: "gray.600" }}
                    onClick={() => setSelectedInvestorType("sophisticated")}
                  >
                    <input
                      type="radio"
                      name="investorType"
                      value="sophisticated"
                      checked={selectedInvestorType === "sophisticated"}
                      onChange={() => setSelectedInvestorType("sophisticated")}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer'
                      }}
                    />
                    {selectedInvestorType === "sophisticated" && (
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="50%"
                        bg="gray.500"
                      />
                    )}
                  </Box>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="gray.500"
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="300"
                  >
                    Sophisticated Investor
                  </Text>
                </Box>

                <Box display="flex" alignItems="center">
                  <Box
                    w="20px"
                    h="20px"
                    borderRadius="50%"
                    border="2px solid"
                    borderColor={selectedInvestorType === "wholesale" ? "gray.500" : "gray.500"}
                    mr={3}
                    cursor="pointer"
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    _hover={{ borderColor: "gray.600" }}
                    onClick={() => setSelectedInvestorType("wholesale")}
                  >
                    <input
                      type="radio"
                      name="investorType"
                      value="wholesale"
                      checked={selectedInvestorType === "wholesale"}
                      onChange={() => setSelectedInvestorType("wholesale")}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer'
                      }}
                    />
                    {selectedInvestorType === "wholesale" && (
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="50%"
                        bg="gray.500"
                      />
                    )}
                  </Box>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="gray.500"
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="300"
                  >
                    Wholesale Investor
                  </Text>
                </Box>

                <Box display="flex" alignItems="center">
                  <Box
                    w="20px"
                    h="20px"
                    borderRadius="50%"
                    border="2px solid"
                    borderColor={selectedInvestorType === "institutional" ? "gray.500" : "gray.500"}
                    mr={3}
                    cursor="pointer"
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    _hover={{ borderColor: "gray.600" }}
                    onClick={() => setSelectedInvestorType("institutional")}
                  >
                    <input
                      type="radio"
                      name="investorType"
                      value="institutional"
                      checked={selectedInvestorType === "institutional"}
                      onChange={() => setSelectedInvestorType("institutional")}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer'
                      }}
                    />
                    {selectedInvestorType === "institutional" && (
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="50%"
                        bg="gray.500"
                      />
                    )}
                  </Box>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="gray.500"
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="300"
                  >
                    Institutional Investor
                  </Text>
                </Box>
              </VStack>
            </Box>

            {/* Sophisticated Investor Certificate Upload (conditional) */}
            {selectedInvestorType === "sophisticated" && (
              <Box mb={8}>
                <Text
                  fontSize="sm"
                  color="gray.500"
                  mb={2}
                  textTransform="uppercase"
                  letterSpacing="1px"
                  fontFamily="'Carmen Sans Light', sans-serif"
                >
                  UPLOAD YOUR SOPHISTICATED INVESTOR CONFIRMATION LETTER / CERTIFICATE *
                </Text>
                <Box
                  borderBottom="1px solid"
                  borderColor="gray.300"
                  pb={2}
                >
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    style={{
                      width: '100%',
                      fontSize: '14px',
                      fontFamily: "'Carmen Sans Light', sans-serif"
                    }}
                  />
                  {certificateFile && (
                    <Text fontSize="xs" color="green.600" mt={2}>
                      ✓ {certificateFile.name} ({(certificateFile.size / 1024).toFixed(2)}KB)
                    </Text>
                  )}
                </Box>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  PDF format only, max 10MB
                </Text>
              </Box>
            )}

            {/* Message Field */}
            <Box mb={8}>
              <Text
                fontSize="sm"
                color="gray.500"
                mb={2}
                textTransform="uppercase"
                letterSpacing="1px"
                fontFamily="'Carmen Sans Light', sans-serif"
              >
                HOW MAY WE ASSIST
              </Text>
              <Box
                borderBottom="1px solid"
                borderColor="gray.300"
                pb={2}
              >
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  style={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '16px',
                    fontFamily: "'Carmen Sans Light', sans-serif",
                    resize: 'vertical',
                    minHeight: '100px'
                  }}
                />
              </Box>
            </Box>

            {/* Acknowledgment Checkbox */}
            <Box mb={8}>
              <Box display="flex" alignItems="flex-start">
                <Box
                  w="20px"
                  h="20px"
                  border="2px solid"
                  borderColor="gray.500"
                  mr={3}
                  mt={1}
                  cursor="pointer"
                  position="relative"
                  flexShrink={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  _hover={{ borderColor: "gray.600" }}
                  onClick={() => setIsAcknowledged(!isAcknowledged)}
                >
                  <input
                    type="checkbox"
                    name="acknowledgment"
                    checked={isAcknowledged}
                    onChange={() => setIsAcknowledged(!isAcknowledged)}
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer'
                    }}
                  />
                  {isAcknowledged && (
                    <Box
                      color="gray.500"
                      fontSize="14px"
                      fontWeight="bold"
                      lineHeight="1"
                    >
                      ✓
                    </Box>
                  )}
                </Box>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color="gray.500"
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                >
                  By checking this box, I acknowledge that this investment product is intended solely institutional and wholesale investors, and I wish to receive the Information Memorandum and Application Form of this product.
                </Text>
              </Box>
            </Box>

              {/* Submit Button */}
              <Box>
                <Box
                  as="button"
                  type="submit"
                  bg="black"
                  color="white"
                  w="full"
                  py={3}
                  borderRadius="md"
                  fontSize="sm"
                  fontWeight="bold"
                  textTransform="uppercase"
                  cursor="pointer"
                  transition="all 0.3s ease"
                  _hover={{ bg: "gray.800" }}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  letterSpacing="1px"
                  disabled={isSubmitting}
                  opacity={isSubmitting ? 0.6 : 1}
                >
                  {isSubmitting ? "Submitting..." : "SUBMIT"}
                </Box>
              </Box>
            </form>
          </Box>

          {/* Right Column - Empty */}
          <Box pl={{ base: 0, lg: 8 }}>
          </Box>
        </SimpleGrid>

        {/* Providence Disclaimer */}
        <Box mt={8}>
          <Text
            fontSize={{ base: "sm", md: "md" }}
            color={textColor}
            fontFamily="'Carmen Sans Light', sans-serif"
            lineHeight="1.4"
          >
            Providence Equity Holdings Pty Ltd ACN 612 005 928 AFSL 487419 is the Trustee of Mercurian Equity Fund.
          </Text>
        </Box>
        </Box>
      </Box>

      <GreshamFooter />

      {/* Submission Success Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent mx={4}>
          <ModalHeader
            fontSize="2xl"
            fontWeight="bold"
            fontFamily="'Carmen Sans Bold', sans-serif"
            pt={8}
            pb={4}
            textAlign="center"
          >
            Thank you
          </ModalHeader>
          <ModalBody pb={6} px={8}>
            <VStack spacing={4} align="center">
              <Text
                fontSize="lg"
                textAlign="center"
                color="gray.700"
                fontFamily="'Carmen Sans Light', sans-serif"
                lineHeight="1.6"
              >
                Your message has been successfully received. A member of our
                team will be in contact with you shortly.
              </Text>
              
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="center" pb={8}>
            <Button
              onClick={onClose}
              bg="black"
              color="white"
              size="lg"
              px={12}
              py={6}
              fontSize="md"
              fontWeight="bold"
              textTransform="uppercase"
              _hover={{ bg: "gray.800" }}
              fontFamily="'Carmen Sans Regular', sans-serif"
            >
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Fund;